import { useState, useEffect, useRef } from 'react';
import { LuX, LuUpload, LuFileText } from 'react-icons/lu';
import { createBatch, notifyAdmins, fetchMyPartner, uploadFile } from '../../lib/directus';
import { SA_TOWNS } from '../../lib/constants';
import styles from './AddBatchDrawer.module.css';

const COMMODITIES = ['Grain', 'Maize', 'Wheat', 'Rice', 'Soybean'];
const GRADES      = ['Grade A', 'Grade B', 'Grade C'];
const UNITS       = ['MT (Bulk)', '50kg Bag'];


export default function AddBatchDrawer({ isOpen, onClose, onSubmit }) {
  const [step, setStep]         = useState(1);
  const [partnerId, setPartnerId] = useState(null);
  const [form, setForm]         = useState({
    commodity: '', quantity: '', unit: 'MT (Bulk)', location: '',
    grade: '', price: '', moisture: '', foreign: '', broken: '',
  });
  const [certs, setCerts]       = useState({ quality: null, weight: null, origin: null });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (isOpen && !partnerId) {
      fetchMyPartner().then(p => { if (p?.id) setPartnerId(p.id); }).catch(console.error);
    }
  }, [isOpen, partnerId]);

  function resetAndClose() {
    setStep(1);
    setForm({ commodity: '', quantity: '', unit: 'MT (Bulk)', location: '', grade: '', price: '', moisture: '', foreign: '', broken: '' });
    setCerts({ quality: null, weight: null, origin: null });
    setError('');
    onClose();
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError('');
    const qualityParts = [];
    if (form.moisture) qualityParts.push(`Moisture: ${form.moisture}%`);
    if (form.foreign)  qualityParts.push(`Foreign: ${form.foreign}%`);
    if (form.broken)   qualityParts.push(`Broken: ${form.broken}%`);
    try {
      const certFiles = [certs.quality, certs.weight, certs.origin].filter(Boolean);
      const certIds = await Promise.all(certFiles.map(c => uploadFile(c.file)));

      const batch = await createBatch({
        commodity:              form.commodity,
        grade:                  form.grade,
        quantity:               parseFloat(form.quantity),
        unit_of_measure:        form.unit,
        status:                 'Pending',
        unit_price:             parseFloat(parseFloat(form.price).toFixed(2)),
        location:               form.location,
        quality_specifications: qualityParts.join('\n') || null,
        certificates:           certIds.map(id => ({ directus_files_id: id })),
        ...(partnerId ? { seller: partnerId } : {}),
      });
      const batchId = batch?.id ? '#BCH-' + String(batch.id).slice(-8).toUpperCase() : 'a new batch';
      notifyAdmins(
        'New batch submitted for approval',
        `A new batch ${batchId} (${form.commodity}${form.grade ? ', ' + form.grade : ''}, ${form.quantity} ${form.unit}) has been submitted and is awaiting your review.`
      ).catch(console.error);
      onSubmit();
      resetAndClose();
    } catch (err) {
      setError(err.message || 'Failed to submit batch. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const step1Valid = form.commodity && form.quantity && form.location && form.grade && form.price;
  const step2Valid = certs.quality && certs.weight;

  function handleFileChange(field, file) {
    if (!file) return;
    setCerts(prev => ({ ...prev, [field]: { file, name: file.name, size: `${(file.size / (1024 * 1024)).toFixed(1)} MB` } }));
  }

  function removeFile(field) {
    setCerts(prev => ({ ...prev, [field]: null }));
  }

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={resetAndClose}>
      <div className={styles.panel} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <p className={styles.title}>{step < 3 ? 'Fill batch details' : 'Review & submit'}</p>
          <button className={styles.closeBtn} onClick={resetAndClose}><LuX size={18} /></button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {step === 1 && (
            <div className={styles.card}>
              {/* Commodity Type */}
              <div className={styles.field}>
                <label className={styles.label}>Commodity Type *</label>
                <select
                  className={styles.select}
                  value={form.commodity}
                  onChange={e => setForm(f => ({ ...f, commodity: e.target.value }))}
                >
                  <option value="">Select type</option>
                  {COMMODITIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>

              {/* Quantity + Unit */}
              <div className={styles.row}>
                <div className={`${styles.field} ${styles.flex1}`}>
                  <label className={styles.label}>Quantity *</label>
                  <input
                    className={styles.input}
                    placeholder="Enter quantity"
                    type="number"
                    min="0"
                    value={form.quantity}
                    onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
                  />
                </div>
                <div className={`${styles.field} ${styles.unitField}`}>
                  <label className={styles.label}>Unit *</label>
                  <select
                    className={styles.select}
                    value={form.unit}
                    onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                  >
                    {UNITS.map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
              </div>

              {/* Warehouse location */}
              <div className={styles.field}>
                <label className={styles.label}>Warehouse location *</label>
                <SearchableDropdown
                  options={SA_TOWNS}
                  value={form.location}
                  onChange={val => setForm(f => ({ ...f, location: val }))}
                  placeholder="Search town..."
                />
              </div>

              {/* Grade */}
              <div className={styles.field}>
                <label className={styles.label}>Grade *</label>
                <select
                  className={styles.select}
                  value={form.grade}
                  onChange={e => setForm(f => ({ ...f, grade: e.target.value }))}
                >
                  <option value="">Select grade</option>
                  {GRADES.map(g => <option key={g}>{g}</option>)}
                </select>
              </div>

              {/* Price */}
              <div className={styles.field}>
                <label className={styles.label}>Unit price (ZAR) *</label>
                <input
                  className={styles.input}
                  placeholder="0.00"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                />
              </div>

              {/* Quality specs */}
              <div className={styles.specsSection}>
                <p className={styles.specsTitle}>Quality specs (optional)</p>
                <div className={styles.specsRow}>
                  <input
                    className={styles.input}
                    placeholder="Moisture %"
                    value={form.moisture}
                    onChange={e => setForm(f => ({ ...f, moisture: e.target.value }))}
                  />
                  <input
                    className={styles.input}
                    placeholder="Foreign %"
                    value={form.foreign}
                    onChange={e => setForm(f => ({ ...f, foreign: e.target.value }))}
                  />
                  <input
                    className={styles.input}
                    placeholder="Broken %"
                    value={form.broken}
                    onChange={e => setForm(f => ({ ...f, broken: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className={styles.card}>
              <p className={styles.cardTitle}>Upload certificates</p>
              <FileUploadRow
                label="Quality certificate *"
                file={certs.quality}
                onChange={f => handleFileChange('quality', f)}
                onRemove={() => removeFile('quality')}
              />
              <FileUploadRow
                label="Weight certificate *"
                file={certs.weight}
                onChange={f => handleFileChange('weight', f)}
                onRemove={() => removeFile('weight')}
              />
              <FileUploadRow
                label="Origin document"
                file={certs.origin}
                onChange={f => handleFileChange('origin', f)}
                onRemove={() => removeFile('origin')}
              />
            </div>
          )}

          {step === 3 && (
            <>
              <div className={styles.card}>
                <div className={styles.reviewGrid}>
                  <div>
                    <p className={styles.reviewLabel}>Commodity</p>
                    <p className={styles.reviewValue}>{form.commodity}</p>
                  </div>
                  <div>
                    <p className={styles.reviewLabel}>Grade</p>
                    <p className={styles.reviewValue}>{form.grade}</p>
                  </div>
                  <div>
                    <p className={styles.reviewLabel}>Quantity</p>
                    <p className={styles.reviewValue}>{form.quantity} {form.unit}</p>
                  </div>
                  <div>
                    <p className={styles.reviewLabel}>Warehouse</p>
                    <p className={styles.reviewValue}>{form.location}</p>
                  </div>
                  <div>
                    <p className={styles.reviewLabel}>Amount</p>
                    <p className={`${styles.reviewValue} ${styles.reviewAmount}`}>R{(parseFloat(form.quantity) * parseFloat(form.price) || 0).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {(form.moisture || form.foreign || form.broken) && (
                <div className={styles.card}>
                  <p className={styles.cardTitle}>Quality specifications</p>
                  <div className={styles.reviewGrid}>
                    {form.moisture && <div><p className={styles.reviewLabel}>Moisture Content</p><p className={styles.reviewValue}>{form.moisture}%</p></div>}
                    {form.foreign  && <div><p className={styles.reviewLabel}>Foreign Matter</p><p className={styles.reviewValue}>{form.foreign}%</p></div>}
                    {form.broken   && <div><p className={styles.reviewLabel}>Broken/Damaged</p><p className={styles.reviewValue}>{form.broken}%</p></div>}
                  </div>
                </div>
              )}

              <div className={styles.card}>
                <p className={styles.cardTitle}>Certificates</p>
                {[certs.quality, certs.weight, certs.origin].filter(Boolean).map((cert, i) => (
                  <div key={i} className={styles.certRow}>
                    <div className={styles.certLeft}>
                      <LuFileText size={16} color="#9ca3af" />
                      <div>
                        <p className={styles.certName}>{cert.name}</p>
                        <p className={styles.certMeta}>{cert.size} • Uploaded</p>
                      </div>
                    </div>
                    <button className={styles.viewLink}>View</button>
                  </div>
                ))}
              </div>

              <div className={styles.infoBanner}>
                <span className={styles.infoIcon}>ⓘ</span>
                After submission, an Admin will verify your batch. This typically takes 1-2 business days.
              </div>
              {error && <p className={styles.errorText}>{error}</p>}
            </>
          )}
        </div>

        {/* Progress */}
        <div className={styles.progressArea}>
          <div className={styles.progressBar}>
            <div className={`${styles.progressSegment} ${step >= 1 ? styles.progressFilled : ''}`} />
            <div className={`${styles.progressSegment} ${step >= 2 ? styles.progressFilled : ''}`} />
            <div className={`${styles.progressSegment} ${step >= 3 ? styles.progressFilled : ''}`} />
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          {step > 1 && (
            <button className={styles.btnPrev} onClick={() => setStep(s => s - 1)}>Previous</button>
          )}
          {step === 1 && (
            <button
              className={`${styles.btnNext} ${step1Valid ? styles.btnNextActive : ''}`}
              onClick={() => step1Valid && setStep(2)}
              disabled={!step1Valid}
            >
              Continue to certificates
            </button>
          )}
          {step === 2 && (
            <button
              className={`${styles.btnNext} ${step2Valid ? styles.btnNextActive : ''}`}
              onClick={() => step2Valid && setStep(3)}
              disabled={!step2Valid}
            >
              Continue to review
            </button>
          )}
          {step === 3 && (
            <button
              className={`${styles.btnNext} ${styles.btnNextActive}`}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit for approval'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SearchableDropdown({ options, value, onChange, placeholder }) {
  const [query, setQuery]   = useState(value || '');
  const [open, setOpen]     = useState(false);
  const ref                 = useRef(null);

  const filtered = query
    ? options.filter(o => o.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSelect(opt) {
    onChange(opt);
    setQuery(opt);
    setOpen(false);
  }

  function handleChange(e) {
    setQuery(e.target.value);
    onChange('');
    setOpen(true);
  }

  return (
    <div className={styles.searchDropdown} ref={ref}>
      <input
        className={styles.searchDropdownInput}
        value={query}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && (
        <div className={styles.searchDropdownList}>
          {filtered.length === 0 ? (
            <div className={styles.searchDropdownEmpty}>No towns found</div>
          ) : (
            filtered.map(opt => (
              <div
                key={opt}
                className={styles.searchDropdownOption}
                onMouseDown={() => handleSelect(opt)}
              >
                {opt}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function FileUploadRow({ label, file, onChange, onRemove }) {
  const ref = useRef(null);
  return (
    <div className={styles.uploadRow}>
      <p className={styles.uploadLabel}>{label}</p>
      {file ? (
        <div className={styles.uploadedFile}>
          <div className={styles.uploadedLeft}>
            <LuFileText size={16} color="#9ca3af" />
            <div>
              <p className={styles.certName}>{file.name}</p>
              <p className={styles.certMeta}>{file.size} • Uploaded</p>
            </div>
          </div>
          <button className={styles.removeFile} onClick={onRemove}><LuX size={14} /></button>
        </div>
      ) : (
        <button className={styles.uploadBtn} onClick={() => ref.current?.click()}>
          <LuUpload size={14} />
          Tap to upload file (PDF, JPG, PNG, 10 MB max)
          <input
            ref={ref}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={e => onChange(e.target.files?.[0])}
          />
        </button>
      )}
    </div>
  );
}
